import React, { useState, useEffect } from 'react';
import { subscriptionAPI, billingAPI } from '../api/userAPI';
import { useAuth } from '../context/AuthContext';
import './billing.css';

const SubscriptionManager = () => {
  const [plans, setPlans] = useState({});
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [upgrading, setUpgrading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [billingPeriod, setBillingPeriod] = useState('monthly');

  const { refreshUser } = useAuth();

  useEffect(() => {
    loadSubscriptionData();
    
    // Check if we're returning from Stripe checkout
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const userId = urlParams.get('user_id');
    
    if (sessionId && userId) {
      handleCheckoutSuccess(sessionId);
    }
  }, []);

  const handleCheckoutSuccess = async (sessionId) => {
    try {
      setLoading(true);
      setError(null);
      
      // Sync the checkout session
      const result = await billingAPI.syncSession(sessionId);
      
      if (result.success) {
        setSuccessMessage(`Successfully upgraded to ${result.tier} plan!`);
        setShowSuccess(true);
        
        // Refresh user data
        await refreshUser();
        await loadSubscriptionData();
        
        // Clear URL parameters
        window.history.replaceState({}, document.title, window.location.pathname);
        
        // Hide success message after 5 seconds
        setTimeout(() => {
          setShowSuccess(false);
        }, 5000);
      } else {
        throw new Error(result.message || 'Failed to sync session');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadSubscriptionData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [plansData, currentData, usageData] = await Promise.all([
        subscriptionAPI.getPlans(),
        subscriptionAPI.getCurrent(),
        subscriptionAPI.getUsage()
      ]);

      setPlans(plansData.plans);
      setCurrentSubscription(currentData);
      setUsage(usageData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (tier) => {
    try {
      setUpgrading(true);
      setError(null);

      // Create Stripe checkout session
      const checkoutResult = await billingAPI.createCheckout(tier, billingPeriod);
      
      if (checkoutResult.success && checkoutResult.url) {
        // Redirect to Stripe checkout
        window.location.href = checkoutResult.url;
      } else {
        throw new Error(checkoutResult.message || 'Failed to create checkout session');
      }
    } catch (err) {
      setError(err.message);
      setUpgrading(false);
    }
  };

  // cancellation is done through stripe portal now (Manage Billing)
  // const handleCancel = async () => {
  //   if (!window.confirm('Are you sure you want to cancel your subscription? You will be downgraded to the free tier.')) {
  //     return;
  //   }

  //   try {
  //     setUpgrading(true);
  //     setError(null);

  //     // Get Stripe subscription status first
  //     const statusResult = await billingAPI.getSubscriptionStatus();
      
  //     if (statusResult.has_stripe_customer && statusResult.subscriptions.length > 0) {
  //       // Cancel via Stripe
  //       const subscription = statusResult.subscriptions[0];
  //       await billingAPI.cancelSubscription(subscription.id);
  //       alert('Subscription cancelled successfully. You will be downgraded to the free tier at the end of your billing period.');
  //     } else {
  //       // Fallback to direct cancellation
  //       await subscriptionAPI.cancel();
  //       alert('Subscription cancelled successfully. You now have free tier access.');
  //     }
      
  //     // Refresh user data to get updated subscription tier
  //     await refreshUser();
  //     await loadSubscriptionData();
  //   } catch (err) {
  //     setError(err.message);
  //   } finally {
  //     setUpgrading(false);
  //   }
  // };

  const handleManageBilling = async () => {
    try {
      setUpgrading(true);
      setError(null);

      // Create Stripe customer portal session
      const portalResult = await billingAPI.createPortal();
      
      if (portalResult.success && portalResult.url) {
        // Redirect to Stripe customer portal
        window.location.href = portalResult.url;
      } else {
        throw new Error(portalResult.message || 'Failed to create portal session');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setUpgrading(false);
    }
  };

  const formatPrice = (price) => {
    return price === 0 ? 'Free' : `$${price}/month`;
  };

  const getUsagePercentage = (current, limit) => {
    if (limit === 0) return 0;
    return Math.min(100, (current / limit) * 100);
  };

  if (loading) {
    return (
      <div className="subscription-manager">
        <div className="loading">Loading subscription information...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="subscription-manager">
        <div className="error-message">
          Error loading subscription data: {error}
        </div>
        <button onClick={loadSubscriptionData} className="btn btn-outline">
          Retry
        </button>
      </div>
    );
  }

  if (showSuccess) {
    return (
      <div className="subscription-manager">
        <div className="success-message">
          <h2>🎉 {successMessage}</h2>
          <p>Your subscription has been updated successfully!</p>
          <button 
            className="btn btn-primary" 
            onClick={() => setShowSuccess(false)}
          >
            Continue
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="subscription-manager">
      <h2>Subscription Management</h2>

      {/* Current Subscription */}
      {currentSubscription && (
        <div className="current-subscription">
          <h3>Current Plan: {currentSubscription.plan?.name}</h3>
          <div className="subscription-details">
            <p><strong>Tier:</strong> {currentSubscription.tier}</p>
            <p><strong>Status:</strong> {currentSubscription.status}</p>
            {currentSubscription.expires_at && (
              <p>
                <strong>
                  {currentSubscription.is_cancelled ? 'Expires' : 'Renews at'}:
                </strong>{' '}
                {new Date(currentSubscription.expires_at).toLocaleDateString()}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Usage Statistics */}
      {usage && (
        <div className="usage-stats">
          <h3>Usage This Month</h3>
          <div className="usage-grid">
            <div className="usage-item">
              <label>API Calls</label>
              <div className="usage-bar">
                <div 
                  className="usage-fill"
                  style={{ 
                    width: `${getUsagePercentage(usage.current_month?.api_calls || 0, usage.subscription_limits?.max_api_calls_per_month || 0)}%` 
                  }}
                ></div>
              </div>
              <span>{usage.current_month?.api_calls || 0} / {usage.subscription_limits?.max_api_calls_per_month || 0}</span>
            </div>

            <div className="usage-item">
              <label>Models Created</label>
              <div className="usage-bar">
                <div 
                  className="usage-fill"
                  style={{ 
                    width: `${getUsagePercentage(usage.current_month?.models_created || 0, usage.subscription_limits?.max_models || 0)}%` 
                  }}
                ></div>
              </div>
              <span>{usage.current_month?.models_created || 0} / {usage.subscription_limits?.max_models || 0}</span>
            </div>

            <div className="usage-item">
              <label>Datasets Uploaded</label>
              <div className="usage-bar">
                <div 
                  className="usage-fill"
                  style={{ 
                    width: `${getUsagePercentage(usage.current_month?.datasets_uploaded || 0, usage.subscription_limits?.max_datasets || 0)}%` 
                  }}
                ></div>
              </div>
              <span>{usage.current_month?.datasets_uploaded || 0} / {usage.subscription_limits?.max_datasets || 0}</span>
            </div>
          </div>
        </div>
      )}

      {/* Available Plans */}
      <div className="subscription-plans">
        <h3>Available Plans</h3>
        <div className="billing-period-toggle">
          <h4>Select Billing Period:
            <button
              className={billingPeriod === 'monthly' ? 'active' : ''}
              onClick={() => setBillingPeriod('monthly')}
            >
              Monthly
            </button>
            <button
              className={billingPeriod === 'yearly' ? 'active' : ''}
              onClick={() => setBillingPeriod('yearly')}
            >
              Annual
            </button>
          </h4>
        </div>
        <div className="plans-grid">
          {Object.entries(plans).filter(([tier]) => tier !== 'super').map(([tier, plan]) => (
            <div key={tier} className={`plan-card ${currentSubscription?.tier === tier ? 'current' : ''}`}>
              <div className="plan-header">
                <h4>{plan.name}</h4>
                <div className="plan-price">
                  <span className="price">{formatPrice(plan.price_monthly)}</span>
                  {plan.price_yearly > 0 && (
                    <span className="yearly-price">${plan.price_yearly}/year (save ${(plan.price_monthly * 12) - plan.price_yearly})</span>
                  )}
                </div>
              </div>

              <div className="plan-description">
                <p>{plan.description}</p>
              </div>

              <div className="plan-features">
                <ul>
                  {plan.features.map((feature, index) => (
                    <li key={index}>{feature}</li>
                  ))}
                </ul>
              </div>

              <div className="plan-limits">
                <div className="limit-item">
                  <span>API Calls:</span>
                  <span>{plan.max_api_calls_per_month != null ? `${plan.max_api_calls_per_month.toLocaleString()}/month` : 'Unlimited'}</span>
                </div>
                <div className="limit-item">
                  <span>Models:</span>
                  <span>{plan.max_models}</span>
                </div>
                <div className="limit-item">
                  <span>Datasets:</span>
                  <span>{plan.max_datasets}</span>
                </div>
                <div className="limit-item">
                  <span>File Size:</span>
                  <span>{plan.max_file_size_mb}MB</span>
                </div>
              </div>

              <div className="plan-actions">
                {currentSubscription?.tier === tier ? (
                  <button className="btn btn-outline" disabled>
                    Current Plan
                  </button>
                ) : (
                  <button
                    className="btn btn-primary"
                    onClick={() => handleUpgrade(tier)}
                    disabled={upgrading}
                  >
                    {upgrading ? 'Upgrading...' : 'Upgrade'}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Billing Management */}
      {currentSubscription?.tier !== 'free' && (
        <div className="billing-management">
          <h3>Billing Management</h3>
          <p>Manage your subscription, payment methods, and billing information through Stripe's secure portal.</p>
          <div className="billing-actions">
            <button
              className="btn btn-outline"
              onClick={handleManageBilling}
              disabled={upgrading}
            >
              {upgrading ? 'Loading...' : 'Manage Billing'}
            </button>
            {/* <button
              className="btn btn-danger"
              onClick={handleCancel}
              disabled={upgrading}
            >
              Cancel Subscription
            </button> */}
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionManager;
